/**
 * HomeV1 — Dark Studio Theme
 * Dark gradient backgrounds, grid overlay, orange primary accents.
 * Supports dark/light mode: light mode gets warmer light bg.
 */
import { Link } from '@inertiajs/react';
import { useHomeContent } from '@/lib/homeContent';
import { ImageCard, VideoCard, ChatDemo, VoiceDemo, SectionBadge, FeatureList, HomeQuickActions, HomeImageTrends, HomeVideoTrends } from './HomeSections';

const isVideoUrl = (url) => /\.(mp4|webm|mov|ogg)(\?.*)?$/i.test(url || '');

export default function HomeV1({ page, openRegister }) {
    const c = useHomeContent(page?.content);

    return (
        <div className="min-h-screen bg-white dark:bg-[#120e0a]">
            {/* Custom CSS */}
            {c.custom_css && <style dangerouslySetInnerHTML={{ __html: c.custom_css }} />}

            {/* ── Hero ─────────────────────────────────────────── */}
            <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-orange-50 via-amber-50 to-slate-100 dark:from-[#120e0a] dark:via-[#1a1410] dark:to-[#0f0c09]">
                {c.hero_bg && (
                    isVideoUrl(c.hero_bg) ? (
                        <video key={c.hero_bg} className="absolute inset-0 h-full w-full object-cover opacity-32 dark:opacity-15" autoPlay loop muted playsInline>
                            <source src={c.hero_bg} type="video/mp4" />
                        </video>
                    ) : (
                        <div className="absolute inset-0 bg-cover bg-center opacity-32 dark:opacity-15" style={{ backgroundImage: `url(${c.hero_bg})` }} />
                    )
                )}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-50/90 via-white/35 to-slate-100/75 dark:from-transparent dark:via-transparent dark:to-transparent" />
                <div className="absolute inset-0 dark:hidden bg-gradient-to-r from-white/55 via-white/10 to-transparent" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent dark:from-primary/20" />
                <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(#ff7b00 1px,transparent 1px),linear-gradient(to right,#ff7b00 1px,transparent 1px)', backgroundSize: '60px 60px' }} />
                <div className="relative z-10 max-w-[1200px] mx-auto px-6 py-24">
                    <div className="max-w-4xl">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 border border-primary/40 rounded-full text-primary text-sm font-bold uppercase tracking-widest mb-8 backdrop-blur">
                            <span className="size-2 bg-primary rounded-full animate-pulse" /> {c.hero_small_title}
                        </div>
                        <h1 className="mb-8 text-6xl font-black leading-[1.02] tracking-tight text-slate-950 drop-shadow-[0_10px_24px_rgba(255,255,255,0.35)] dark:text-white dark:drop-shadow-none md:text-8xl"
                            dangerouslySetInnerHTML={{ __html: c.hero_main_title }} />
                        <p className="mb-12 max-w-2xl text-xl leading-relaxed text-slate-700 dark:text-white/60">{c.hero_description}</p>
                        <div className="flex flex-wrap gap-4">
                            {c.hero_btn_1_url ? (
                                <a href={c.hero_btn_1_url} className="bg-primary hover:bg-primary/90 text-black px-10 py-4 rounded-xl font-black text-lg transition-all shadow-2xl shadow-primary/40 hover:scale-105">
                                    {c.hero_btn_1_text}
                                </a>
                            ) : (
                                <button onClick={openRegister} className="bg-primary hover:bg-primary/90 text-black px-10 py-4 rounded-xl font-black text-lg transition-all shadow-2xl shadow-primary/40 hover:scale-105">
                                    {c.hero_btn_1_text}
                                </button>
                            )}
                            <Link href={c.hero_btn_2_url} className="rounded-xl border border-white/80 bg-white/80 px-10 py-4 text-lg font-bold text-slate-900 shadow-lg shadow-orange-100/70 transition-all backdrop-blur hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-white dark:shadow-none dark:hover:bg-white/10">
                                {c.hero_btn_2_text}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Stats ─────────────────────────────────────────── */}
            <section className="py-16 bg-gradient-to-b from-slate-100 to-white dark:from-[#1a1612] dark:to-[#1a1410]">
                <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="text-center group">
                            <div className="text-4xl md:text-5xl font-black text-primary mb-2 group-hover:scale-110 transition-transform">{c[`stat_number_${i}`]}</div>
                            <div className="text-sm text-slate-500 dark:text-white/40 uppercase tracking-wider">{c[`stat_label_${i}`]}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── Intro ─────────────────────────────────────────── */}
            {c.show_intro !== false && (
                <section className="py-24 bg-white dark:bg-[#1a1410]">
                    <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                        <div className="space-y-8 text-center lg:text-left order-2 lg:order-1">
                            <div className="inline-block px-5 py-2 bg-primary/20 border border-primary rounded-full text-primary text-sm font-bold uppercase tracking-wider">{c.intro_badge}</div>
                            <h2 className="text-5xl md:text-7xl font-black leading-[1.05] tracking-tight text-slate-900 dark:text-white"
                                dangerouslySetInnerHTML={{ __html: c.intro_title }} />
                            <p className="text-slate-600 dark:text-white/60 text-xl leading-relaxed" dangerouslySetInnerHTML={{ __html: c.intro_desc }} />
                            <div className="flex flex-wrap justify-center lg:justify-start gap-6 pt-4">
                                {[c.intro_check_1, c.intro_check_2].filter(Boolean).map((t, i) => (
                                    <div key={i} className="flex items-center gap-3 text-slate-700 dark:text-white/80">
                                        <span className="material-symbols-outlined text-primary text-2xl">check_circle</span>
                                        <span className="font-semibold">{t}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="relative order-1 lg:order-2">
                            <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-3xl" />
                            <div className="relative bg-gradient-to-br from-white to-slate-100 dark:from-[#28211b] dark:to-[#1a1410] rounded-3xl overflow-hidden aspect-video border-2 border-slate-200 dark:border-white/10 shadow-2xl">
                                <video className="absolute inset-0 w-full h-full object-cover" autoPlay loop muted playsInline>
                                    <source src={c.intro_video} type="video/mp4" />
                                </video>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* ── Quick Actions ────────────────────────────────────────── */}
            {c.show_quick_actions !== false && (
                <section className="py-24 bg-white dark:bg-[#120e0a]">
                    <div className="max-w-[1400px] mx-auto px-6 space-y-12">
                        <div className="text-center space-y-4 max-w-3xl mx-auto">
                            <SectionBadge icon="bolt" text={c.quick_actions_badge || 'AI Models'} />
                            <h2 className="text-5xl md:text-6xl font-black tracking-tight text-slate-900 dark:text-white" dangerouslySetInnerHTML={{ __html: c.quick_actions_title || 'Explore AI Capability' }} />
                            <p className="text-slate-600 dark:text-white/50 text-lg leading-relaxed">{c.quick_actions_desc || 'Access all premium generation models and tools instantly from your dashboard.'}</p>
                        </div>
                        <HomeQuickActions />
                    </div>
                </section>
            )}

            {/* ── Image Trends ──────────────────────────────────────── */}
            {c.show_trends !== false && (
                <section className="py-24 bg-gradient-to-b from-white to-slate-50 dark:from-[#120e0a] dark:to-[#1a1612]">
                    <div className="max-w-[1400px] mx-auto px-6 space-y-12">
                        <div className="text-center space-y-4 max-w-3xl mx-auto">
                            <SectionBadge icon="auto_awesome" text={c.trends_badge || 'Trending'} />
                            <h2 className="text-5xl md:text-6xl font-black tracking-tight text-slate-900 dark:text-white" dangerouslySetInnerHTML={{ __html: c.trends_title || 'Discover <span class="text-primary">AI Image Trends</span>' }} />
                            <p className="text-slate-600 dark:text-white/50 text-lg leading-relaxed">{c.trends_desc || 'Explore our most popular AI templates and creative presets.'}</p>
                        </div>
                        <HomeImageTrends />
                    </div>
                </section>
            )}

            {/* ── Image Studio ──────────────────────────────────── */}
            {c.show_images !== false && (
                <section className="py-24 bg-gradient-to-b from-white via-slate-50 to-slate-100 dark:from-[#1a1612] dark:to-[#1a1612]">
                    <div className="max-w-[1200px] mx-auto px-6 space-y-16">
                        <div className="text-center space-y-6 max-w-3xl mx-auto">
                            <SectionBadge icon="image" text={c.image_studio_badge} />
                            <h2 className="text-5xl md:text-6xl font-black tracking-tight text-slate-900 dark:text-white" dangerouslySetInnerHTML={{ __html: c.image_studio_title }} />
                            <p className="text-slate-600 dark:text-white/50 text-lg leading-relaxed">{c.image_studio_desc}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {c.image_cards.map((item, i) => (
                                <ImageCard key={i} item={item}
                                    cardClass="bg-gradient-to-br from-white to-slate-50 dark:from-[#28211b] dark:to-[#1a1410] border-slate-200 dark:border-white/5"
                                    overlayClass="bg-white/20 dark:bg-black/40" />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── Video Trends ──────────────────────────────────── */}
            {c.show_video_trends !== false && (
                <section className="py-24 bg-gradient-to-b from-slate-100 to-white dark:from-[#1a1612] dark:to-[#120e0a]">
                    <div className="max-w-[1400px] mx-auto px-6 space-y-16">
                        <div className="text-center space-y-6 max-w-3xl mx-auto">
                            <SectionBadge icon="smart_display" text={c.video_trends_badge || 'Video Trends'} />
                            <h2 className="text-5xl md:text-6xl font-black tracking-tight text-slate-900 dark:text-white" dangerouslySetInnerHTML={{ __html: c.video_trends_title || 'Trending <span class="text-primary">Avatar Templates</span>' }} />
                            <p className="text-slate-600 dark:text-white/50 text-lg leading-relaxed">{c.video_trends_desc || 'Preview popular avatar video templates before creating your own.'}</p>
                        </div>
                        <HomeVideoTrends />
                    </div>
                </section>
            )}

            {/* ── Video Studio ──────────────────────────────────── */}
            {c.show_videos !== false && (
                <section className="py-24 bg-gradient-to-b from-slate-100 to-white dark:from-[#1a1612] dark:to-[#120e0a]">
                    <div className="max-w-[1200px] mx-auto px-6 space-y-16">
                        <div className="text-center space-y-6 max-w-3xl mx-auto">
                            <SectionBadge icon="movie" text={c.video_studio_badge} />
                            <h2 className="text-5xl md:text-6xl font-black tracking-tight text-slate-900 dark:text-white" dangerouslySetInnerHTML={{ __html: c.video_studio_title }} />
                            <p className="text-slate-600 dark:text-white/50 text-lg">{c.video_studio_desc}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {c.video_cards.map((item, i) => (
                                <VideoCard key={i} item={item}
                                    cardClass="bg-gradient-to-br from-white to-slate-50 dark:from-[#28211b] dark:to-[#1a1410] border-slate-200 dark:border-white/5" />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── Chat ──────────────────────────────────────────── */}
            {c.show_chat !== false && (
                <section className="py-24 bg-white dark:bg-[#120e0a]">
                    <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="space-y-8 order-2 lg:order-1">
                            <SectionBadge icon="chat" text={c.chat_badge} />
                            <h2 className="text-5xl md:text-6xl font-black tracking-tight text-slate-900 dark:text-white" dangerouslySetInnerHTML={{ __html: c.chat_title }} />
                            <p className="text-slate-600 dark:text-white/60 text-xl leading-relaxed">{c.chat_desc}</p>
                            <FeatureList items={c.chat_features} />
                        </div>
                        <div className="relative order-1 lg:order-2">
                            <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-3xl" />
                            <ChatDemo panelClass="bg-gradient-to-br from-white to-slate-100 dark:from-[#28211b] dark:to-[#1a1410] border-slate-200 dark:border-white/10" />
                        </div>
                    </div>
                </section>
            )}

            {/* ── Voice ─────────────────────────────────────────── */}
            {c.show_voice !== false && (
                <section className="py-24 bg-gradient-to-b from-slate-50 to-slate-100 dark:from-[#1a1612] dark:to-[#120e0a]">
                    <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="relative">
                            <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-3xl" />
                            <VoiceDemo panelClass="bg-gradient-to-br from-white to-slate-100 dark:from-[#28211b] dark:to-[#1a1410] border-slate-200 dark:border-white/10" />
                        </div>
                        <div className="space-y-8">
                            <SectionBadge icon="graphic_eq" text={c.voice_badge} />
                            <h2 className="text-5xl md:text-6xl font-black tracking-tight text-slate-900 dark:text-white" dangerouslySetInnerHTML={{ __html: c.voice_title }} />
                            <p className="text-slate-600 dark:text-white/60 text-xl leading-relaxed">{c.voice_desc}</p>
                            <FeatureList items={c.voice_features} />
                        </div>
                    </div>
                </section>
            )}

            {/* ── CTA ───────────────────────────────────────────── */}
            <section className="py-32 bg-gradient-to-b from-slate-100 to-white dark:from-[#1a1612] dark:to-[#120e0a] relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent dark:from-primary/10" />
                <div className="max-w-[800px] mx-auto px-6 text-center relative z-10">
                    <h2 className="text-6xl md:text-7xl font-black tracking-tight mb-8 text-slate-900 dark:text-white">
                        {c.cta_title} <span className="text-primary">{c.cta_subtitle}</span>?
                    </h2>
                    <p className="text-slate-600 dark:text-white/60 text-xl mb-12 max-w-2xl mx-auto">{c.cta_desc}</p>
                    <div className="flex flex-wrap justify-center gap-6">
                        <button onClick={openRegister} className="bg-primary hover:bg-primary/90 text-black px-16 py-6 rounded-xl font-black text-xl transition-all shadow-2xl shadow-primary/40 hover:scale-105">
                            {c.cta_btn_primary}
                        </button>
                        <Link href={c.hero_btn_2_url} className="bg-primary/20 hover:bg-primary/30 text-slate-900 dark:text-white px-16 py-6 rounded-xl font-bold text-xl border-2 border-primary transition-all">
                            {c.cta_btn_secondary}
                        </Link>
                    </div>
                    <p className="text-slate-400 dark:text-white/30 text-sm mt-8">{c.cta_tagline}</p>
                </div>
            </section>
        </div>
    );
}
